import { createContext, useCallback, useContext, useState } from 'react'
import { IVerticalBlockItem } from '../../types'

type Video = {
  index: number
  anchorId: string
  title: string
} & IVerticalBlockItem

interface IBigBrotherContext {
  videos: IVerticalBlockItem[]
  registerVideo: (video: Video) => void
  showPlayer: boolean
  handleShowPlayer: (index: number) => void
  handleClosePlayer: (index: number) => void
}

const defaultState = {
  videos: [],
  registerVideo: () => {
    return
  },
  showPlayer: false,
  handleShowPlayer: () => {
    return
  },
  handleClosePlayer: () => {
    return
  },
} as IBigBrotherContext

interface IBigBrotherProps {
  children: React.ReactNode
}

export const BigBrotherContext = createContext(defaultState)

export const BigBrotherProvider = ({ children }: IBigBrotherProps) => {
  const [videos, setVideos] = useState<Video[]>([])
  const [showPlayer, setShowPlayer] = useState(false)

  const handleShowPlayer = useCallback(
    (index: number) => {
      const video = videos.find(v => v.index === index)
      if (video) {
        setVideos(prev => [video, ...prev.filter(v => v.index !== index)])
      }
      setShowPlayer(true)
    },
    [videos]
  )

  const handleClosePlayer = useCallback(
    (_: number, kalturaId?: string) => {
      const video = videos.find(v => v.video.kalturaId === kalturaId)
      if (video) {
        const anchorId = video.anchorId
        const anchor = document.getElementById(anchorId)
        if (anchor) {
          anchor.scrollIntoView({ behavior: 'smooth' })
        }
      }
      setVideos(prev => prev.sort((a, b) => a.index - b.index))
      setShowPlayer(false)
    },
    [videos]
  )

  const registerVideo = useCallback((video: Video) => {
    setVideos(prev =>
      prev.some(v => v.video.kalturaId === video.video.kalturaId)
        ? prev
        : [...prev, video]
    )
  }, [])

  return (
    <BigBrotherContext.Provider
      value={{
        videos,
        registerVideo,
        showPlayer,
        handleShowPlayer,
        handleClosePlayer,
      }}
    >
      {children}
    </BigBrotherContext.Provider>
  )
}

export const useBigBrother = () => {
  const context = useContext(BigBrotherContext)
  if (context === undefined) {
    throw new Error('useBigBrother must be used within a BigBrotherProvider')
  }
  return context
}
